import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  PanelLoadReport,
  PanelRow,
  PanelRowId,
  PanelWarning,
  RowLoad,
  Section,
  SectionId,
} from "@electro-planner/domain";
import {
  interpretCableCode,
  interpretComponentCode,
  suggestComponents,
} from "@electro-planner/components";
import type { PanelMutations } from "../hooks/useDomainMutations.ts";
import type { LinkedDevice, LinkedProject } from "../linkedProjects/registry.ts";
import {
  LoadsEditor,
  LoadsSummary,
  useLoadsEditorState,
} from "./LoadsEditor.tsx";

/**
 * Tabell-editor för centralspec. Varje cell är ett input-fält; Tab/Enter/
 * piltangenter navigerar mellan celler. Enter på sista raden lägger till en
 * ny rad. Kortkoder (DB, JFB, JFA, kabel som "5G2,5") tolkas vid blur eller
 * när användaren väljer ett förslag.
 *
 * Sektionering: rader grupperas under S-kod-header som renderas som en egen
 * rad i tabellen. Osektionerade rader hamnar sist under "(Ej sektionerade)".
 */

const COLUMNS = [
  { key: "designation", label: "Bet.", width: 64 },
  { key: "component", label: "Komponent", width: 180 },
  { key: "ampRating", label: "A", width: 56 },
  { key: "poles", label: "Pol", width: 48 },
  { key: "characteristic", label: "Kar.", width: 48 },
  { key: "cable", label: "Kabel", width: 140 },
  { key: "cableNumber", label: "Kabelnr", width: 80 },
  { key: "usage", label: "Användning", width: 200 },
  { key: "loads", label: "Laster", width: 200 },
  { key: "loadWatts", label: "Last (W)", width: 72 },
  { key: "notes", label: "Notering", width: 180 },
] as const;

type ColumnKey = (typeof COLUMNS)[number]["key"];
/** Kolumn-nycklar som är editable input-celler (allt utom "loads"). */
type CellKey = Exclude<ColumnKey, "loads">;

export type PanelTableProps = {
  rows: ReadonlyArray<PanelRow>;
  sections: ReadonlyArray<Section>;
  warnings: ReadonlyArray<PanelWarning>;
  load: PanelLoadReport;
  mutations: PanelMutations;
  linkedProject: LinkedProject | null;
};

type Group = {
  section: Section | null; // null = osektionerade
  rows: PanelRow[];
};

export function PanelTable({
  rows,
  sections,
  warnings,
  load,
  mutations,
  linkedProject,
}: PanelTableProps) {
  const cellRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const [autocompleteFor, setAutocompleteFor] = useState<string | null>(null);
  const loadsEditor = useLoadsEditorState();

  // När raden i popoven har uppdaterats i Yjs får vi en fräsch row-instans
  // tillbaka via projection → props. Synka popoven mot den aktuella raden
  // så siffror och auto-säkrings-badgen uppdateras live.
  const openLoadsEditorRow = loadsEditor.openFor
    ? rows.find((r) => r.id === loadsEditor.openFor!.id) ?? null
    : null;

  // Säkerställ att en tom panel har minst en rad (annars ser tabellen tom ut).
  useEffect(() => {
    if (rows.length === 0) {
      mutations.addRow();
    }
  }, [rows.length, mutations]);

  const warningsByRow = useMemo(() => {
    const m = new Map<string, PanelWarning[]>();
    for (const w of warnings) {
      const arr = m.get(w.rowId) ?? [];
      arr.push(w);
      m.set(w.rowId, arr);
    }
    return m;
  }, [warnings]);

  // Gruppera rader: sektioner först (i sin order), sedan osektionerade.
  const groups = useMemo<Group[]>(() => {
    const bySection = new Map<SectionId, PanelRow[]>();
    const unsectioned: PanelRow[] = [];
    for (const r of rows) {
      if (r.sectionId) {
        const arr = bySection.get(r.sectionId) ?? [];
        arr.push(r);
        bySection.set(r.sectionId, arr);
      } else {
        unsectioned.push(r);
      }
    }
    const result: Group[] = sections.map((s) => ({
      section: s,
      rows: (bySection.get(s.id) ?? []).sort((a, b) => a.order - b.order),
    }));
    if (unsectioned.length > 0 || sections.length === 0) {
      result.push({ section: null, rows: unsectioned.sort((a, b) => a.order - b.order) });
    }
    return result;
  }, [rows, sections]);

  // Platta rad-lista i samma ordning som de ritas — används för keyboard-nav.
  const flatRows = useMemo(() => groups.flatMap((g) => g.rows), [groups]);

  // Varje grupp börjar på ett offset i flatRows. Precomputa så SectionBlock
  // kan få rätt startIdx utan att mutera räknare under render.
  const groupStartIdx = useMemo(() => {
    const out: number[] = [];
    let n = 0;
    for (const g of groups) {
      out.push(n);
      n += g.rows.length;
    }
    return out;
  }, [groups]);

  const registerCell = useCallback(
    (rowId: string, col: CellKey) => (el: HTMLInputElement | null) => {
      const key = `${rowId}:${col}`;
      if (el) cellRefs.current.set(key, el);
      else cellRefs.current.delete(key);
    },
    [],
  );

  const focusCell = useCallback((rowId: string, col: CellKey) => {
    const el = cellRefs.current.get(`${rowId}:${col}`);
    if (el) {
      el.focus();
      el.select();
    }
  }, []);

  const advance = useCallback(
    (rowIdx: number, colIdx: number, dir: "up" | "down" | "left" | "right") => {
      let r = rowIdx;
      let c = colIdx;
      if (dir === "down") r = Math.min(rowIdx + 1, flatRows.length - 1);
      if (dir === "up") r = Math.max(rowIdx - 1, 0);
      if (dir === "left") c = Math.max(colIdx - 1, 0);
      if (dir === "right") c = Math.min(colIdx + 1, COLUMNS.length - 1);
      // Hoppa över non-input-kolumner (Laster är en knapp, inte en input).
      while (COLUMNS[c]?.key === "loads") {
        if (dir === "right" && c < COLUMNS.length - 1) c++;
        else if (dir === "left" && c > 0) c--;
        else break;
      }
      const target = flatRows[r];
      const key = COLUMNS[c]?.key;
      if (target && key && key !== "loads") focusCell(target.id, key);
    },
    [flatRows, focusCell],
  );

  const onKeyDown = useCallback(
    (
      e: React.KeyboardEvent<HTMLInputElement>,
      rowIdx: number,
      colIdx: number,
      row: PanelRow,
    ) => {
      // onKeyDown triggas bara från Cell-inputs, aldrig från Laster-knappen.
      // Därför är colKey alltid en CellKey, vi narrowar via assertion.
      const colKey = COLUMNS[colIdx]!.key as CellKey;

      // Tangentbordsgenvägar med Ctrl — tas tidigt så de fungerar oavsett cell.
      if (e.ctrlKey || e.metaKey) {
        const k = e.key.toLowerCase();
        if (k === "i" && !e.shiftKey) {
          e.preventDefault();
          const newIds = mutations.insertRowsAfter(row.id, 1);
          if (newIds[0]) {
            requestAnimationFrame(() => focusCell(newIds[0]!, "designation"));
          }
          return;
        }
        if (k === "5" && !e.shiftKey) {
          e.preventDefault();
          const newIds = mutations.insertRowsAfter(row.id, 5);
          if (newIds[0]) {
            requestAnimationFrame(() => focusCell(newIds[0]!, "designation"));
          }
          return;
        }
        if (k === "e" && !e.shiftKey) {
          e.preventDefault();
          mutations.removeRow(row.id);
          return;
        }
        if (k === "d" && !e.shiftKey) {
          e.preventDefault();
          const dupId = mutations.duplicateRow(row.id);
          if (dupId) requestAnimationFrame(() => focusCell(dupId, "designation"));
          return;
        }
        if (k === "c" && e.shiftKey) {
          e.preventDefault();
          const prev = flatRows[rowIdx - 1];
          if (prev) copyRowValuesFrom(prev, row, mutations);
          return;
        }
      }

      // Indent-hantering: Tab i "Bet."-cellen ökar, Shift+Tab minskar.
      if (e.key === "Tab" && colKey === "designation") {
        if (e.shiftKey) {
          const currentIndent = row.indent ?? 0;
          if (currentIndent > 0) {
            e.preventDefault();
            mutations.indentRow(row.id, -1);
            return;
          }
        } else if (rowIdx > 0) {
          // Öka indent om användaren är i Bet.-cellen och det finns en rad ovan.
          // Håll kvar default Tab-beteende (till nästa cell) om redan på max.
          const currentIndent = row.indent ?? 0;
          if (currentIndent < 3) {
            e.preventDefault();
            mutations.indentRow(row.id, 1);
            return;
          }
        }
      }

      if (
        (e.key === "Enter" || e.key === "Tab") &&
        autocompleteFor === `${row.id}:${colKey}`
      ) {
        const raw = (e.target as HTMLInputElement).value;
        const applied = applyShortCode(raw, colKey, row, mutations);
        if (applied) {
          e.preventDefault();
          setAutocompleteFor(null);
          if (e.key === "Enter") advance(rowIdx, colIdx, "down");
          else advance(rowIdx, colIdx, "right");
          return;
        }
      }

      switch (e.key) {
        case "Enter":
          e.preventDefault();
          if (rowIdx === flatRows.length - 1) {
            // Ny rad hamnar i samma sektion som sista raden (användaren jobbar
            // linjärt nedåt — de flesta vill fortsätta inom pågående sektion).
            const newId = mutations.addRowToSection(row.sectionId ?? null);
            requestAnimationFrame(() => focusCell(newId, colKey));
          } else {
            advance(rowIdx, colIdx, "down");
          }
          break;
        case "Tab":
          if (!e.shiftKey && colIdx === COLUMNS.length - 1) {
            if (rowIdx === flatRows.length - 1) {
              e.preventDefault();
              const newId = mutations.addRowToSection(row.sectionId ?? null);
              requestAnimationFrame(() => focusCell(newId, "designation"));
            }
          }
          break;
        case "ArrowDown":
          e.preventDefault();
          advance(rowIdx, colIdx, "down");
          break;
        case "ArrowUp":
          e.preventDefault();
          advance(rowIdx, colIdx, "up");
          break;
        case "ArrowRight":
          if (
            (e.target as HTMLInputElement).selectionEnd ===
            (e.target as HTMLInputElement).value.length
          ) {
            e.preventDefault();
            advance(rowIdx, colIdx, "right");
          }
          break;
        case "ArrowLeft":
          if ((e.target as HTMLInputElement).selectionStart === 0) {
            e.preventDefault();
            advance(rowIdx, colIdx, "left");
          }
          break;
        case "Escape":
          setAutocompleteFor(null);
          (e.target as HTMLInputElement).blur();
          break;
      }
    },
    [flatRows, mutations, focusCell, advance, autocompleteFor],
  );

  return (
    <div className="relative flex-1 overflow-auto">
      <table className="w-full border-collapse text-sm">
        <thead className="sticky top-0 z-20 bg-slate-100 text-left">
          <tr>
            <th className="w-10 border border-slate-300 px-2 py-1 text-xs text-slate-500">
              #
            </th>
            <th
              className="w-8 border border-slate-300 px-1 py-1 text-center text-xs text-slate-500"
              title="Granskad"
            >
              ✓
            </th>
            {COLUMNS.map((c) => (
              <th
                key={c.key}
                className="border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700"
                style={{ minWidth: c.width }}
              >
                {c.label}
              </th>
            ))}
            <th className="w-20 border border-slate-300 px-1 py-1 text-xs text-slate-500">
              Sektion
            </th>
            <th className="w-10 border border-slate-300 px-1 py-1"></th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group, gi) => {
            const sectionLoad = group.section
              ? load.bySection.get(group.section.id)
              : load.unsectioned;
            return (
              <SectionBlock
                key={group.section?.id ?? "unsectioned"}
                group={group}
                sections={sections}
                sectionLoad={sectionLoad}
                mutations={mutations}
                warningsByRow={warningsByRow}
                registerCell={registerCell}
                focusCell={focusCell}
                onKeyDown={onKeyDown}
                autocompleteFor={autocompleteFor}
                setAutocompleteFor={setAutocompleteFor}
                startVisualIdx={groupStartIdx[gi] ?? 0}
                linkedProject={linkedProject}
                openLoadsEditor={loadsEditor.open}
              />
            );
          })}
        </tbody>
      </table>

      <div className="flex items-center gap-2 p-2">
        <button
          type="button"
          onClick={() => {
            const id = mutations.addSection();
            void id;
          }}
          className="rounded border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50"
        >
          + Lägg till sektion
        </button>
        <button
          type="button"
          onClick={() => {
            const id = mutations.addRow();
            requestAnimationFrame(() => focusCell(id, "designation"));
          }}
          className="rounded border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50"
        >
          + Lägg till rad
        </button>
      </div>

      {openLoadsEditorRow && loadsEditor.anchor ? (
        <LoadsEditor
          row={openLoadsEditorRow}
          anchor={loadsEditor.anchor}
          onClose={loadsEditor.close}
          onChange={(loads: ReadonlyArray<RowLoad>) =>
            mutations.setRowLoads(openLoadsEditorRow.id, loads)
          }
          onToggleAuto={(on) =>
            mutations.setRowFuseAuto(openLoadsEditorRow.id, on)
          }
        />
      ) : null}
    </div>
  );
}

/**
 * Cell i "Laster"-kolumnen. Visar kompakt sammanfattning + öppnar popover-
 * editor vid klick. Rader med `fuseAuto` får en liten AUTO-badge så det syns
 * att ampRating/karakteristik är härledda från laster-listan.
 */
function LoadsCell({
  row,
  onOpen,
}: {
  row: PanelRow;
  onOpen: (el: HTMLElement) => void;
}) {
  const loads = row.loads ?? [];
  return (
    <td className="border border-slate-300 px-0 py-0">
      <button
        type="button"
        onClick={(e) => onOpen(e.currentTarget)}
        className="flex w-full items-center gap-1 px-2 py-1 text-left hover:bg-sky-50"
        title={loads.length > 0 ? "Redigera laster" : "Lägg till laster"}
      >
        {loads.length === 0 ? (
          <span className="text-xs text-slate-400">+ Lägg till last</span>
        ) : (
          <>
            <LoadsSummary loads={loads} />
            {row.fuseAuto ? (
              <span className="ml-auto shrink-0 rounded bg-sky-100 px-1 text-[10px] font-semibold text-sky-800">
                AUTO
              </span>
            ) : null}
          </>
        )}
      </button>
    </td>
  );
}

/**
 * Renderar en sektion: header-rad + alla rader i sektionen. Skiljer sig från
 * PanelTableRow genom att ta emot `flatRowIdx` (global index i flatRows) så
 * keyboard-nav kan slå upp rätt rad.
 */
function SectionBlock({
  group,
  sections,
  sectionLoad,
  mutations,
  warningsByRow,
  registerCell,
  focusCell,
  onKeyDown,
  autocompleteFor,
  setAutocompleteFor,
  startVisualIdx,
  linkedProject,
  openLoadsEditor,
}: {
  group: Group;
  sections: ReadonlyArray<Section>;
  sectionLoad: { watts: number; amps: number; rowCount: number } | undefined;
  mutations: PanelMutations;
  warningsByRow: Map<string, PanelWarning[]>;
  registerCell: (
    rowId: string,
    col: CellKey,
  ) => (el: HTMLInputElement | null) => void;
  focusCell: (rowId: string, col: CellKey) => void;
  onKeyDown: (
    e: React.KeyboardEvent<HTMLInputElement>,
    rowIdx: number,
    colIdx: number,
    row: PanelRow,
  ) => void;
  autocompleteFor: string | null;
  setAutocompleteFor: (v: string | null) => void;
  startVisualIdx: number;
  linkedProject: LinkedProject | null;
  openLoadsEditor: (row: PanelRow, el: HTMLElement) => void;
}) {
  return (
    <>
      <SectionHeaderRow
        section={group.section}
        rowCount={group.rows.length}
        load={sectionLoad}
        onRename={(patch) => {
          if (group.section) mutations.patchSectionMeta(group.section.id, patch);
        }}
        onAddRow={() => {
          const id = mutations.addRowToSection(group.section?.id ?? null);
          requestAnimationFrame(() => focusCell(id, "designation"));
        }}
        onDuplicate={
          group.section
            ? () => {
                mutations.duplicateSection(group.section!.id);
              }
            : undefined
        }
        onRemove={
          group.section
            ? () => {
                if (
                  confirm(
                    `Ta bort sektion ${group.section!.code}? Raderna blir osektionerade.`,
                  )
                ) {
                  mutations.removeSection(group.section!.id);
                }
              }
            : undefined
        }
      />
      {group.rows.map((row, i) => (
        <PanelTableRow
          key={row.id}
          row={row}
          rowIdx={startVisualIdx + i}
          visualNumber={startVisualIdx + i + 1}
          sections={sections}
          warnings={warningsByRow.get(row.id) ?? []}
          onKeyDown={onKeyDown}
          onChange={(patch) => mutations.updateRow(row.id, patch)}
          onRemove={() => mutations.removeRow(row.id)}
          onDuplicate={() => {
            const dup = mutations.duplicateRow(row.id);
            if (dup) requestAnimationFrame(() => focusCell(dup, "designation"));
          }}
          onMoveSection={(sectionId) =>
            mutations.moveRowToSection(row.id, sectionId)
          }
          onOpenLoads={(el) => openLoadsEditor(row, el)}
          registerCell={registerCell}
          autocompleteFor={autocompleteFor}
          setAutocompleteFor={setAutocompleteFor}
          onApply={(raw, col) => applyShortCode(raw, col, row, mutations)}
          linkedProject={linkedProject}
        />
      ))}
    </>
  );
}

function SectionHeaderRow({
  section,
  rowCount,
  load,
  onRename,
  onAddRow,
  onDuplicate,
  onRemove,
}: {
  section: Section | null;
  rowCount: number;
  load: { watts: number; amps: number; rowCount: number } | undefined;
  onRename: (patch: { code?: string; label?: string }) => void;
  onAddRow: () => void;
  onDuplicate?: () => void;
  onRemove?: () => void;
}) {
  const totalCols = COLUMNS.length + 4; // #, granskad, columns, sektion, actions
  return (
    <tr className="bg-sky-50">
      <td
        colSpan={totalCols}
        className="border border-slate-300 px-2 py-1 text-sm"
      >
        <div className="flex flex-wrap items-center gap-2">
          {section ? (
            <>
              <input
                type="text"
                value={section.code}
                onChange={(e) => onRename({ code: e.target.value })}
                className="w-14 rounded border border-slate-200 bg-white px-1 py-0.5 font-mono text-xs font-semibold"
                aria-label="Sektionskod"
              />
              <input
                type="text"
                value={section.label ?? ""}
                placeholder="t.ex. Belysning vardagsrum"
                onChange={(e) => onRename({ label: e.target.value })}
                className="min-w-[16rem] flex-1 rounded border border-slate-200 bg-white px-2 py-0.5 text-sm"
                aria-label="Sektionsbeskrivning"
              />
            </>
          ) : (
            <span className="font-mono text-xs font-semibold text-slate-500">
              (Ej sektionerade)
            </span>
          )}
          <span className="text-xs text-slate-500">
            {rowCount} rad{rowCount === 1 ? "" : "er"}
            {load && load.watts > 0
              ? ` · ${Math.round(load.watts)} W · ${load.amps.toFixed(1)} A`
              : ""}
          </span>
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={onAddRow}
              className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs hover:bg-slate-50"
            >
              + rad
            </button>
            {onDuplicate ? (
              <button
                type="button"
                onClick={onDuplicate}
                className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-600 hover:bg-sky-50 hover:text-sky-700"
                title="Duplicera hela sektionen"
              >
                ⎘ Duplicera
              </button>
            ) : null}
            {onRemove ? (
              <button
                type="button"
                onClick={onRemove}
                className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-500 hover:bg-red-50 hover:text-red-600"
                title="Ta bort sektion"
              >
                ×
              </button>
            ) : null}
          </div>
        </div>
      </td>
    </tr>
  );
}

type RowProps = {
  row: PanelRow;
  rowIdx: number;
  visualNumber: number;
  sections: ReadonlyArray<Section>;
  warnings: ReadonlyArray<PanelWarning>;
  onKeyDown: (
    e: React.KeyboardEvent<HTMLInputElement>,
    rowIdx: number,
    colIdx: number,
    row: PanelRow,
  ) => void;
  onChange: (patch: Partial<Omit<PanelRow, "id">>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onMoveSection: (sectionId: SectionId | null) => void;
  onOpenLoads: (triggerEl: HTMLElement) => void;
  registerCell: (
    rowId: string,
    col: CellKey,
  ) => (el: HTMLInputElement | null) => void;
  autocompleteFor: string | null;
  setAutocompleteFor: (v: string | null) => void;
  onApply: (raw: string, col: ColumnKey) => boolean;
  linkedProject: LinkedProject | null;
};

function PanelTableRow({
  row,
  rowIdx,
  visualNumber,
  sections,
  warnings,
  onKeyDown,
  onChange,
  onRemove,
  onDuplicate,
  onMoveSection,
  onOpenLoads,
  registerCell,
  autocompleteFor,
  setAutocompleteFor,
  onApply,
  linkedProject,
}: RowProps) {
  const usageSuggestions =
    autocompleteFor === `${row.id}:usage`
      ? suggestUsageFromLinked(row.usage ?? "", linkedProject, 6)
      : [];
  const warnByCol = useMemo(() => {
    const m = new Map<string, PanelWarning[]>();
    for (const w of warnings) {
      const arr = m.get(w.column) ?? [];
      arr.push(w);
      m.set(w.column, arr);
    }
    return m;
  }, [warnings]);

  const cellWarn = (col: ColumnKey): PanelWarning | undefined => {
    if (col === "cable") return warnByCol.get("cable")?.[0];
    return warnByCol.get(col as PanelWarning["column"])?.[0];
  };

  return (
    <tr className={`group ${row.reviewed ? "bg-emerald-50/40" : ""}`}>
      <td className="border border-slate-300 bg-slate-50 px-2 py-1 text-xs text-slate-400">
        {visualNumber}
      </td>

      <td className="border border-slate-300 bg-slate-50 px-0 py-0 text-center">
        <input
          type="checkbox"
          checked={row.reviewed ?? false}
          onChange={(e) => onChange({ reviewed: e.target.checked || undefined })}
          className="cursor-pointer"
          title="Markera raden som granskad"
          aria-label="Granskad"
        />
      </td>

      <Cell
        value={row.designation}
        placeholder=""
        warn={cellWarn("designation")}
        inputRef={registerCell(row.id, "designation")}
        onChange={(v) => onChange({ designation: v })}
        onKeyDown={(e) => onKeyDown(e, rowIdx, 0, row)}
        indent={row.indent ?? 0}
      />

      <Cell
        value={row.component ?? ""}
        placeholder="t.ex. DB16"
        warn={cellWarn("component")}
        inputRef={registerCell(row.id, "component")}
        onChange={(v) => {
          onChange({ component: v });
          setAutocompleteFor(v.length > 0 ? `${row.id}:component` : null);
        }}
        onBlur={(v) => {
          const applied = onApply(v, "component");
          if (applied) setAutocompleteFor(null);
        }}
        onKeyDown={(e) => onKeyDown(e, rowIdx, 1, row)}
        suggestions={
          autocompleteFor === `${row.id}:component`
            ? suggestComponents(row.component ?? "", 5).map((c) => ({
                key: c.key,
                label: c.label,
              }))
            : []
        }
        onSuggestionClick={(key) => {
          onApply(key, "component");
          setAutocompleteFor(null);
        }}
      />

      <Cell
        value={row.ampRating !== undefined ? String(row.ampRating) : ""}
        placeholder=""
        warn={cellWarn("ampRating")}
        inputRef={registerCell(row.id, "ampRating")}
        onChange={(v) => onChange({ ampRating: parseNum(v) })}
        onKeyDown={(e) => onKeyDown(e, rowIdx, 2, row)}
      />

      <Cell
        value={row.poles !== undefined ? String(row.poles) : ""}
        placeholder=""
        inputRef={registerCell(row.id, "poles")}
        onChange={(v) => {
          const n = parseNum(v);
          onChange({
            poles: n === 1 || n === 2 || n === 3 || n === 4 ? n : undefined,
          });
        }}
        onKeyDown={(e) => onKeyDown(e, rowIdx, 3, row)}
      />

      <Cell
        value={row.characteristic ?? ""}
        placeholder=""
        inputRef={registerCell(row.id, "characteristic")}
        onChange={(v) => {
          const up = v.toUpperCase();
          onChange({
            characteristic: up === "B" || up === "C" || up === "D" ? up : undefined,
          });
        }}
        onKeyDown={(e) => onKeyDown(e, rowIdx, 4, row)}
      />

      <Cell
        value={formatCable(row)}
        placeholder="5G2,5"
        warn={cellWarn("cable")}
        inputRef={registerCell(row.id, "cable")}
        onChange={(v) => onChange({ cableType: v })}
        onBlur={(v) => onApply(v, "cable")}
        onKeyDown={(e) => onKeyDown(e, rowIdx, 5, row)}
      />

      <Cell
        value={row.cableNumber ?? ""}
        placeholder=""
        inputRef={registerCell(row.id, "cableNumber")}
        onChange={(v) => onChange({ cableNumber: v || undefined })}
        onKeyDown={(e) => onKeyDown(e, rowIdx, 6, row)}
      />

      <Cell
        value={row.usage ?? ""}
        placeholder=""
        warn={cellWarn("usage")}
        inputRef={registerCell(row.id, "usage")}
        onChange={(v) => {
          onChange({ usage: v || undefined });
          setAutocompleteFor(
            v.length > 0 && linkedProject ? `${row.id}:usage` : null,
          );
        }}
        onFocus={() => {
          if (linkedProject && (row.usage ?? "").length === 0) {
            setAutocompleteFor(`${row.id}:usage`);
          }
        }}
        onBlur={() => {
          if (autocompleteFor === `${row.id}:usage`) setAutocompleteFor(null);
        }}
        onKeyDown={(e) => onKeyDown(e, rowIdx, 7, row)}
        suggestions={usageSuggestions}
        onSuggestionClick={(key) => {
          onChange({ usage: key });
          setAutocompleteFor(null);
        }}
      />

      <LoadsCell row={row} onOpen={onOpenLoads} />

      <Cell
        value={row.loadWatts !== undefined ? String(row.loadWatts) : ""}
        placeholder=""
        inputRef={registerCell(row.id, "loadWatts")}
        onChange={(v) => onChange({ loadWatts: parseNum(v) })}
        onKeyDown={(e) => onKeyDown(e, rowIdx, 9, row)}
      />

      <Cell
        value={row.notes ?? ""}
        placeholder=""
        inputRef={registerCell(row.id, "notes")}
        onChange={(v) => onChange({ notes: v || undefined })}
        onKeyDown={(e) => onKeyDown(e, rowIdx, 10, row)}
      />

      <td className="border border-slate-300 px-0 py-0">
        <select
          value={row.sectionId ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            onMoveSection(v ? (v as SectionId) : null);
          }}
          className="w-full border-0 bg-transparent px-1 py-1 text-xs"
        >
          <option value="">—</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.code}
            </option>
          ))}
        </select>
      </td>

      <td className="border border-slate-300 px-0 py-0 text-center">
        <div className="flex items-center justify-center gap-0.5 opacity-0 group-hover:opacity-100">
          <button
            type="button"
            onClick={onDuplicate}
            className="rounded px-1 text-slate-400 hover:bg-sky-50 hover:text-sky-600"
            title="Duplicera rad (Ctrl+D)"
          >
            ⎘
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="rounded px-1 text-slate-400 hover:bg-rose-50 hover:text-red-500"
            title="Ta bort rad (Ctrl+E)"
          >
            ×
          </button>
        </div>
      </td>
    </tr>
  );
}

type CellProps = {
  value: string;
  placeholder?: string;
  warn?: PanelWarning;
  inputRef: (el: HTMLInputElement | null) => void;
  onChange: (v: string) => void;
  onBlur?: (v: string) => void;
  onFocus?: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  suggestions?: ReadonlyArray<{ key: string; label: string }>;
  onSuggestionClick?: (key: string) => void;
  /** Indent-nivå för hierarkiska rader (0 = huvud). Lägger till padding-left. */
  indent?: number;
};

function Cell({
  value,
  placeholder,
  warn,
  inputRef,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  suggestions,
  onSuggestionClick,
  indent,
}: CellProps) {
  const isDuplicate = warn?.code === "DESIGNATION_DUPLICATE";
  const bgClass = warn
    ? isDuplicate
      ? "bg-rose-50"
      : "bg-amber-50"
    : "";
  const borderClass = isDuplicate ? "outline outline-2 -outline-offset-2 outline-rose-400" : "";
  const indentStyle =
    indent && indent > 0
      ? { paddingLeft: `${0.5 + indent * 1.25}rem` }
      : undefined;
  return (
    <td className={`relative border border-slate-300 px-0 py-0 ${bgClass} ${borderClass}`}>
      <input
        ref={inputRef}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => onBlur?.(e.target.value)}
        onFocus={() => onFocus?.()}
        onKeyDown={onKeyDown}
        title={warn ? `${warn.message}${warn.reference ? ` (${warn.reference})` : ""}` : undefined}
        style={indentStyle}
        className="panel-cell w-full border-0 bg-transparent px-2 py-1 outline-none focus:bg-white focus:ring-1 focus:ring-sky-400"
      />
      {suggestions && suggestions.length > 0 ? (
        <ul className="absolute left-0 right-0 top-full z-30 mt-0.5 max-h-48 overflow-auto rounded border border-slate-300 bg-white shadow-lg">
          {suggestions.map((s) => (
            <li key={s.key}>
              <button
                type="button"
                className="block w-full px-2 py-1 text-left text-xs hover:bg-sky-50"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSuggestionClick?.(s.key);
                }}
              >
                {s.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </td>
  );
}

function parseNum(v: string): number | undefined {
  if (v.trim() === "") return undefined;
  const n = parseFloat(v.replace(",", "."));
  return Number.isFinite(n) ? n : undefined;
}

function formatCable(row: PanelRow): string {
  if (row.cableType && row.cableCores && row.cableArea !== undefined) {
    const area = String(row.cableArea).replace(".", ",");
    return `${row.cableType} ${row.cableCores}G${area}`;
  }
  return row.cableType ?? "";
}

/**
 * Bygger förslag till "Användning"-kolumnen från ett kopplat schemaeditor-
 * projekt. Prioriterar befintlig `usage` på apparaten, annars beteckning +
 * label. Filter på prefix (case-insensitive) i användarens input.
 */
function suggestUsageFromLinked(
  input: string,
  project: LinkedProject | null,
  limit: number,
): ReadonlyArray<{ key: string; label: string }> {
  if (!project) return [];
  const needle = input.trim().toLowerCase();
  const out: { key: string; label: string }[] = [];
  const seen = new Set<string>();
  for (const d of project.devices) {
    const suggestion = buildUsageSuggestion(d);
    if (!suggestion) continue;
    const { key, label } = suggestion;
    if (seen.has(key)) continue;
    if (needle && !key.toLowerCase().includes(needle) && !label.toLowerCase().includes(needle)) {
      continue;
    }
    seen.add(key);
    out.push({ key, label });
    if (out.length >= limit) break;
  }
  return out;
}

function buildUsageSuggestion(d: LinkedDevice): { key: string; label: string } | null {
  const usage = d.usage?.trim();
  if (usage) {
    const meta = [d.designation, d.label].filter(Boolean).join(" · ");
    return { key: usage, label: meta ? `${usage}  (${meta})` : usage };
  }
  const fallback = d.label?.trim() || d.designation;
  if (!fallback) return null;
  return {
    key: fallback,
    label: d.designation && d.label ? `${d.label}  (${d.designation})` : fallback,
  };
}

/**
 * Kopierar värden från källrad (raden ovan) till målrad. Motsvarar
 * enlinje.se:s "Kopiera special" — tar med även autokompletterade värden
 * (komponent, A, poler, karakteristik, kabel, användning).
 */
function copyRowValuesFrom(
  src: PanelRow,
  dst: PanelRow,
  mutations: PanelMutations,
): void {
  mutations.updateRow(dst.id, {
    component: src.component,
    ampRating: src.ampRating,
    poles: src.poles,
    characteristic: src.characteristic,
    sensitivity: src.sensitivity,
    rcdType: src.rcdType,
    cableType: src.cableType,
    cableCores: src.cableCores,
    cableArea: src.cableArea,
    usage: src.usage,
    loadWatts: src.loadWatts,
  });
}

/**
 * Tolkar användarens input i antingen "component"- eller "cable"-kolumnen och
 * applicerar patchen. Returnerar true om något tolkades och applicerades.
 */
function applyShortCode(
  raw: string,
  col: ColumnKey,
  row: PanelRow,
  mutations: PanelMutations,
): boolean {
  if (col === "component") {
    const s = interpretComponentCode(raw);
    if (!s) return false;
    mutations.updateRow(row.id, s.patch);
    return true;
  }
  if (col === "cable") {
    const s = interpretCableCode(raw);
    if (!s) return false;
    mutations.updateRow(row.id, s.patch);
    return true;
  }
  return false;
}
